import React, { useRef, useEffect, useState } from "react";
import Head from "next/head";

import styles from "./index.module.scss";
import Header from "./Header";
import Content from "./Content";
import Breadcrumbs from "../Breadcrumbs";
import useWindowWidth from "hooks/useWindowWidth";

export default function DetailPage({ article }) {
  const width = useWindowWidth();
  const [isVisible, setIsVisible] = useState(false);
  const titleRef = useRef(null);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const checkVisibility = () => {
    if (titleRef.current) {
      const rect = titleRef.current.getBoundingClientRect();
      setIsVisible(rect.top < 0);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", checkVisibility);
    return () => window.removeEventListener("scroll", checkVisibility);
  }, []);

  const BlogPostJSON = {
    "@context": "http://schema.org/",
    "@type": "BlogPosting",
    mainEntityOfPage: {
      "@type": "WebPage",
      url: `https://apphud.com/blog/${article.identifier}`,
    },
    author: {
      "@type": "Person",
      name: `${article.user?.first_name} ${article.user?.last_name}`,
    },
    publisher: {
      "@type": "Organization",
      name: "Apphud",
      logo: {
        "@type": "ImageObject",
        url: "https://apphud.com/images/logo-header.svg",
      },
    },
    headline: `${article.title}`,
    image: `${article?.icon.original}`,
    datePublished: `${article.published_at || article.created_at}`,
    dateModified: `${article.updated_at || article.created_at}`,
    description: `${article.pretext}`,
    keywords: article.tags.map(({ title }) => title),
  };

  const BredCrumbsList = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@id": `https://apphud.com/blog/category/${article.category.identifier}`,
          name: `${article.category.title}`,
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@id": `https://apphud.com/blog/${article.identifier}`,
          name: `${article.title}`,
        },
      },
    ],
  };

  const breadcrumbsItems = [
    {
      title: "Blog",
      url: "/blog",
    },
    {
      title: article.category.title,
      url: `/blog/categories/${article.category.identifier}`,
    },
    {
      title: article.title,
      url: "",
    },
  ];

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(BlogPostJSON),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(BredCrumbsList),
          }}
        />
        <title>{article.meta_title || article.title}</title>
      </Head>
      {width > 1022 && <Breadcrumbs items={breadcrumbsItems} />}
      <Header
        title={article.title}
        date={article.published_at || article.created_at}
        readingTime={article.reading_time}
        description={article.pretext}
        bgColor={article.color}
        avatar={article.user?.avatar.thumb}
        author={`${article.user?.first_name} ${article.user?.last_name} `}
        image={article?.icon.original}
        locales={[article.locale, ...article.locales].sort()}
        nextPost={article?.next_post}
        prevPost={article?.previous_post}
        titleRef={titleRef}
      />
      <Content
        banner={article.banner}
        blocks={article.body.blocks}
        tags={article.tags}
        author={article.user}
      />
      {isVisible && (
        <div onClick={scrollToTop} className={styles.scrollToTop}>
          <img
            src="/blog/scroll_to_top.svg"
            alt="scroll-to-top"
            width={102}
            height={102}
          />
        </div>
      )}
    </>
  );
}
